import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import {
  Activity, AlertTriangle, CheckCircle2, RefreshCw, Clock, Gauge,
  Euro, Cpu, AlertOctagon, Sparkles, ExternalLink, ShieldAlert,
  Info, PowerOff, Ban,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmtEUR = (v) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(v ?? 0);

const fmtInt = (v) => (v ?? 0).toLocaleString('nl-NL');

function fmtMs(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function fmtMinAgo(min) {
  if (min == null) return null;
  if (min < 1) return 'zojuist';
  if (min < 60) return `${min} min geleden`;
  if (min < 60 * 24) return `${Math.floor(min / 60)} u geleden`;
  return `${Math.floor(min / (60 * 24))} d geleden`;
}

function fmtAbsoluteTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function pluralJob(n) {
  return `${fmtInt(n)} job${n === 1 ? '' : 's'}`;
}

// "Needs attention" classifier — surfaces silent issues, not just loud ones.
// As of policy v2 this is computed BY THE BACKEND in /summary. The FE just
// reads ``row.attention.has`` and shows ``row.attention.reasons``. Kept here
// as a deliberate no-op so the call sites still type-check and a future
// regression where the BE forgets the field would surface immediately.
function attentionForRow(row) {
  return row?.attention || { has: false, reasons: [], severity: 'none' };
}

// Operational status chip mapping
const OP_STATUS = {
  active: { label: 'Actief', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  ai_off: { label: 'AI uit', cls: 'bg-slate-100 text-slate-600 border-slate-300', icon: PowerOff },
  cap_reached: { label: 'Budget op', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Ban },
};

function OpStatusChip({ status }) {
  const m = OP_STATUS[status] || OP_STATUS.active;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${m.cls}`}>
      <Icon size={10} /> {m.label}
    </span>
  );
}

// Small info-tooltip (CSS-only, no portal needed)
function InfoTip({ text, testid }) {
  return (
    <span className="relative inline-flex group ml-1 align-middle" data-testid={testid}>
      <Info size={12} className="text-slate-400 cursor-help" />
      <span className="invisible group-hover:visible absolute bottom-full left-1/2 z-10 mb-1 w-64 -translate-x-1/2 rounded bg-slate-900 px-2 py-1 text-[10px] font-normal normal-case tracking-normal text-white shadow-lg">
        {text}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Small atomic components
// ---------------------------------------------------------------------------
function KpiCard({ icon: Icon, label, value, sublabel, tone = 'neutral', testid }) {
  const tones = {
    neutral: 'bg-slate-50 text-slate-600',
    good: 'bg-emerald-50 text-emerald-700',
    warn: 'bg-amber-50 text-amber-700',
    bad: 'bg-rose-50 text-rose-700',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
          <p className="mt-2 text-2xl font-bold text-slate-900 tabular-nums" data-testid={`${testid}-value`}>
            {value}
          </p>
          {sublabel && <p className="mt-1 text-xs text-slate-500">{sublabel}</p>}
        </div>
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`}>
          <Icon size={16} />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ bucket, subBucket }) {
  // Prefer subBucket when present for finer-grained labeling.
  const key = subBucket || bucket;
  const map = {
    ok: { label: 'OK', cls: 'bg-emerald-100 text-emerald-800' },
    fallback: { label: 'Fallback', cls: 'bg-amber-100 text-amber-800' },
    fail: { label: 'Fout', cls: 'bg-rose-100 text-rose-800' },
    fail_real: { label: 'Echte fout', cls: 'bg-rose-100 text-rose-800' },
    refused_disabled: { label: 'AI uit', cls: 'bg-slate-100 text-slate-600' },
    refused_capped: { label: 'Budget op', cls: 'bg-amber-100 text-amber-700' },
  };
  const m = map[key] || { label: key || '—', cls: 'bg-slate-100 text-slate-700' };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}>
      {m.label}
    </span>
  );
}

function BackendBadge({ backend }) {
  const m = {
    anthropic: 'bg-violet-50 text-violet-700 border-violet-200',
    emergent: 'bg-sky-50 text-sky-700 border-sky-200',
    unknown: 'bg-slate-50 text-slate-500 border-slate-200',
  }[backend] || 'bg-slate-50 text-slate-500 border-slate-200';
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${m}`}>
      {backend || 'unknown'}
    </span>
  );
}

function ProgressBar({ percent, danger }) {
  const clamped = Math.min(100, Math.max(0, percent || 0));
  const color = clamped >= 100 ? 'bg-rose-500' : clamped >= 80 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full ${color} ${danger ? '' : ''}`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

function Section({ title, subtitle, icon: Icon, children, action, testid }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={testid}>
      <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="text-slate-500" />}
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          {subtitle && <span className="text-xs text-slate-500">· {subtitle}</span>}
        </div>
        {action}
      </header>
      <div>{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function AIHealth() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [latency, setLatency] = useState(null);
  const [cost, setCost] = useState(null);
  const [recent, setRecent] = useState(null);
  const [errs, setErrs] = useState(null);
  const [recentLocation, setRecentLocation] = useState(''); // '' = all

  const load = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const recentParams = { limit: 20 };
      if (recentLocation) recentParams.location = recentLocation;
      const [s, l, c, r, e] = await Promise.all([
        api.get('/ai-health/summary', { params: { days: 7 } }),
        api.get('/ai-health/latency', { params: { days: 7 } }),
        api.get('/ai-health/cost'),
        api.get('/ai-health/recent', { params: recentParams }),
        api.get('/ai-health/errors', { params: { days: 7, top: 5 } }),
      ]);
      setSummary(s.data);
      setLatency(l.data);
      setCost(c.data);
      setRecent(r.data);
      setErrs(e.data);
    } catch (ex) {
      setError(ex?.response?.data?.detail || ex.message || 'Onbekende fout');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [recentLocation]);

  useEffect(() => { load(); }, [load]);

  // ----- Derived: attention rows (backend-computed) ---------------------
  const attentionRows = useMemo(() => {
    if (!summary?.per_location) return [];
    const rows = summary.per_location.filter((r) => r.attention?.has);
    // Sort high → medium → low; within tier by fail_real desc
    const sevOrder = { high: 0, medium: 1, low: 2, none: 3 };
    rows.sort((a, b) => {
      const sa = sevOrder[a.attention.severity] ?? 4;
      const sb = sevOrder[b.attention.severity] ?? 4;
      if (sa !== sb) return sa - sb;
      return (b.fail_real || 0) - (a.fail_real || 0);
    });
    return rows;
  }, [summary]);

  // Map cost rows by location for the per-location combined table
  const costByLoc = useMemo(() => {
    const m = {};
    (cost?.per_location || []).forEach((r) => { m[r.location_id] = r; });
    return m;
  }, [cost]);

  // Map latency end-to-end by location
  const e2eByLoc = useMemo(() => {
    const m = {};
    (latency?.end_to_end?.per_location || []).forEach((r) => { m[r.location_id] = r; });
    return m;
  }, [latency]);

  // ----- Render ---------------------------------------------------------
  return (
    <div className="space-y-5" data-testid="page-ai-health">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <Sparkles size={18} className="text-brand-amber" /> AI Gezondheid
          </h1>
          <p className="text-xs text-slate-500">
            Observability over de AI-roostergenerator — laatste 7 dagen (kosten: huidige maand).
            Alleen-lezen, raakt de pipeline niet aan.
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
          data-testid="ai-health-refresh-btn"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Vernieuwen
        </button>
      </div>

      {/* Error / loading */}
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="ai-health-error">
          {String(error)}
        </div>
      )}
      {loading ? (
        <p className="text-sm text-slate-500">Laden…</p>
      ) : !summary ? (
        <p className="text-sm text-slate-500">Geen data beschikbaar.</p>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              testid="kpi-total-calls"
              icon={Activity}
              label={<>AI-calls (7d) <InfoTip testid="kpi-7d-tip" text="Alle ai_jobs in de laatste 7 dagen, inclusief refusals (422 AI uit, 429 budget op) en faliure-vóór-LLM. Bron: ai_jobs (TTL 7d)." /></>}
              value={fmtInt(summary.totals.total)}
              sublabel={`Sinds ${fmtAbsoluteTime(summary.since)}`}
              tone="neutral"
            />
            <KpiCard
              testid="kpi-success-rate"
              icon={CheckCircle2}
              label={<>Geslaagd <InfoTip testid="kpi-attempted-tip" text="Noemer = ok + fallback + echte fouten. Refusals (AI uitgezet / budget op) tellen niet als poging." /></>}
              value={`${summary.totals.success_rate_pct}%`}
              sublabel={`${fmtInt(summary.totals.ok)} van ${fmtInt(summary.totals.attempted)} pogingen`}
              tone={summary.totals.success_rate_pct >= 90 ? 'good' : summary.totals.success_rate_pct >= 70 ? 'warn' : 'bad'}
            />
            <KpiCard
              testid="kpi-fallback-rate"
              icon={ShieldAlert}
              label="Stil opgevangen (fallback)"
              value={`${summary.totals.fallback_rate_pct}%`}
              sublabel={`${pluralJob(summary.totals.fallback)} gebruikten template-rollout`}
              tone={summary.totals.fallback === 0 ? 'good' : summary.totals.fallback >= 2 ? 'bad' : 'warn'}
            />
            <KpiCard
              testid="kpi-cost-month"
              icon={Euro}
              label={<>Kosten {cost?.year_month || ''} <InfoTip testid="kpi-cost-tip" text={`Maand-aggregaat uit ai_usage_tracking. Andere noemer dan 'AI-calls (7d)': dit is de hele maand én telt alleen calls die de LLM hebben bereikt (refusals tellen niet mee). Daarom kunnen 7d-calls en maand-calls verschillen.`} /></>}
              value={fmtEUR(cost?.totals?.cost_eur ?? 0)}
              sublabel={`${fmtInt(cost?.totals?.calls_count ?? 0)} calls · €/USD = ${cost?.eur_per_usd ?? 0.92}`}
              tone="neutral"
            />
          </div>

          {/* Attention banner — front and center */}
          <Section
            testid="ai-health-attention"
            icon={AlertTriangle}
            title="Aandacht nodig"
            subtitle={
              attentionRows.length
                ? `${attentionRows.length} locatie${attentionRows.length === 1 ? '' : 's'} met signaal`
                : 'alle locaties OK'
            }
          >
            {attentionRows.length === 0 ? (
              <div className="flex items-center gap-2 px-4 py-6 text-sm text-emerald-700" data-testid="ai-health-attention-empty">
                <CheckCircle2 size={16} /> Geen echte storingen, fallback-patroon, of verdachte output in de laatste 7 dagen. Dormancy (locaties die niet draaiden) wordt bewust niet als signaal gemeld.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {attentionRows.map((row) => (
                  <li
                    key={row.location_id}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                    data-testid={`attention-row-${row.location_id}`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{row.location_id}</p>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider ${
                          row.attention.severity === 'high' ? 'text-rose-700' :
                          row.attention.severity === 'medium' ? 'text-amber-700' : 'text-slate-500'
                        }`}>
                          {row.attention.severity}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {row.attention.reasons.map((iss, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800"
                          >
                            <AlertOctagon size={11} /> {iss}
                          </span>
                        ))}
                      </div>
                      {row.quality_warning_reasons?.length > 0 && (
                        <ul className="mt-1.5 ml-1 list-disc pl-4 text-[11px] text-slate-600 space-y-0.5">
                          {row.quality_warning_reasons.slice(0, 3).map((qw, i) => (
                            <li key={i}>
                              <span className="font-medium">{qw.reason === 'empty_plan' ? 'leeg plan' : 'veel gedropt'}</span>
                              : proposed {qw.proposed}, gedropt {qw.dropped} ({Math.round(qw.drop_ratio * 100)}%),
                              geschreven {qw.inserted + qw.overwritten}, al-aanwezig {qw.skipped_existing}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      <p className="tabular-nums">
                        {row.attempted} call{row.attempted === 1 ? '' : 's'} (excl. refusals) ·
                        ok {row.ok} · fb {row.fallback} · echte fout {row.fail_real}
                      </p>
                      <p>
                        Laatste succes:{' '}
                        <span className={row.last_ok_at ? 'text-slate-700' : 'font-semibold text-rose-700'}>
                          {row.last_ok_at
                            ? `${fmtMinAgo(row.minutes_since_last_ok)} (${fmtAbsoluteTime(row.last_ok_at)})`
                            : 'nog nooit gelukt'}
                        </span>
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Per-location overview */}
          <Section
            testid="ai-health-per-location"
            icon={Gauge}
            title="Per locatie — laatste 7 dagen"
            subtitle="ok / fallback / fout · laatste succes · end-to-end latency · kosten deze maand"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Locatie</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2 text-right">Calls</th>
                    <th className="px-4 py-2 text-right">OK</th>
                    <th className="px-4 py-2 text-right">Fallback</th>
                    <th className="px-4 py-2 text-right">Echte fout</th>
                    <th className="px-4 py-2">Laatste succes</th>
                    <th className="px-4 py-2 text-right">p50 E2E</th>
                    <th className="px-4 py-2 text-right">p95 E2E</th>
                    <th className="px-4 py-2 text-right">Kost · cap</th>
                  </tr>
                </thead>
                <tbody>
                  {(summary.per_location.length === 0) ? (
                    <tr><td colSpan={10} className="px-4 py-6 text-center text-sm text-slate-500">Nog geen AI-calls in dit venster.</td></tr>
                  ) : summary.per_location.map((row) => {
                    const e2e = e2eByLoc[row.location_id];
                    const c = costByLoc[row.location_id];
                    const needsAttn = !!row.attention?.has;
                    return (
                      <tr
                        key={row.location_id}
                        className={`border-t border-slate-100 ${needsAttn ? 'bg-rose-50/30' : ''}`}
                        data-testid={`per-loc-row-${row.location_id}`}
                      >
                        <td className="px-4 py-2 font-medium text-slate-900">{row.location_id}</td>
                        <td className="px-4 py-2"><OpStatusChip status={row.operational_status} /></td>
                        <td className="px-4 py-2 text-right tabular-nums">{row.total}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-emerald-700">{row.ok}</td>
                        <td className={`px-4 py-2 text-right tabular-nums ${row.fallback ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}>{row.fallback}</td>
                        <td className={`px-4 py-2 text-right tabular-nums ${row.fail_real ? 'text-rose-700 font-semibold' : 'text-slate-400'}`}>{row.fail_real}</td>
                        <td className="px-4 py-2 text-xs">
                          {row.last_ok_at ? (
                            <span className="text-slate-600" title={fmtAbsoluteTime(row.last_ok_at)}>
                              {fmtMinAgo(row.minutes_since_last_ok)}
                            </span>
                          ) : row.operational_status === 'ai_off' ? (
                            <span className="text-slate-400">n.v.t.</span>
                          ) : (
                            <span className="font-semibold text-rose-700">nog nooit gelukt</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-slate-600">{fmtMs(e2e?.p50_ms)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-slate-600">{fmtMs(e2e?.p95_ms)}</td>
                        <td className="px-4 py-2 text-right text-xs">
                          {c ? (
                            <div className="space-y-1">
                              <div className="tabular-nums text-slate-700">
                                {fmtEUR(c.cost_eur)} <span className="text-slate-400">/ {fmtEUR(c.cap_eur)}</span>
                              </div>
                              <ProgressBar percent={c.percent_used} />
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Latency — explicit dual labels */}
          <Section
            testid="ai-health-latency"
            icon={Clock}
            title="Snelheid (latency)"
            subtitle="Twee verschillende metingen — lees ze niet door elkaar"
          >
            <div className="grid gap-0 md:grid-cols-2 md:divide-x md:divide-slate-100">
              <div className="p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">LLM-tijd alleen</p>
                <p className="mb-3 text-xs text-slate-500">Puur Anthropic streaming-tijd ({'>'} 95% van de wachttijd). Geen parse, geen DB-write.</p>
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-slate-500">
                    <tr><th className="py-1">Locatie</th><th>Backend</th><th className="text-right">p50</th><th className="text-right">p95</th><th className="text-right">n</th></tr>
                  </thead>
                  <tbody>
                    {(latency?.llm_only?.per_location_backend || []).length === 0 ? (
                      <tr><td colSpan={5} className="py-3 text-center text-xs text-slate-400">Geen geslaagde calls.</td></tr>
                    ) : latency.llm_only.per_location_backend.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`latency-llm-${r.location_id}-${r.backend}`}>
                        <td className="py-1.5">{r.location_id}</td>
                        <td><BackendBadge backend={r.backend} /></td>
                        <td className="text-right tabular-nums">{fmtMs(r.p50_ms)}</td>
                        <td className="text-right tabular-nums">{fmtMs(r.p95_ms)}</td>
                        <td className="text-right tabular-nums text-slate-500">{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">End-to-end</p>
                <p className="mb-3 text-xs text-slate-500">Van start tot afronding: LLM + parse + DB-write. Dit is wat de bedrijfsleider voelt.</p>
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-slate-500">
                    <tr><th className="py-1">Locatie</th><th className="text-right">p50</th><th className="text-right">p95</th><th className="text-right">max</th><th className="text-right">n</th></tr>
                  </thead>
                  <tbody>
                    {(latency?.end_to_end?.per_location || []).length === 0 ? (
                      <tr><td colSpan={5} className="py-3 text-center text-xs text-slate-400">Geen geslaagde calls.</td></tr>
                    ) : latency.end_to_end.per_location.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`latency-e2e-${r.location_id}`}>
                        <td className="py-1.5">{r.location_id}</td>
                        <td className="text-right tabular-nums">{fmtMs(r.p50_ms)}</td>
                        <td className="text-right tabular-nums">{fmtMs(r.p95_ms)}</td>
                        <td className="text-right tabular-nums text-slate-500">{fmtMs(r.max_ms)}</td>
                        <td className="text-right tabular-nums text-slate-500">{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* stop reasons */}
            {summary.stop_reasons && Object.keys(summary.stop_reasons).length > 0 && (
              <div className="border-t border-slate-100 px-4 py-3 text-xs">
                <span className="font-semibold uppercase tracking-wide text-slate-500">Stop-reason histogram: </span>
                {Object.entries(summary.stop_reasons).map(([k, v]) => (
                  <span
                    key={k}
                    className={`mr-2 inline-flex items-center rounded-full px-2 py-0.5 ${
                      k === 'max_tokens' ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-700'
                    }`}
                    data-testid={`stop-reason-${k}`}
                  >
                    {k}: {v}
                  </span>
                ))}
                <span className="text-slate-500">
                  · <span className="font-medium">max_tokens</span> is een vroegtijdig waarschuwing voor output-truncatie.
                </span>
              </div>
            )}
          </Section>

          {/* Cost (preview honesty) */}
          <Section
            testid="ai-health-cost"
            icon={Euro}
            title={`Kosten · ${cost?.year_month || ''}`}
            subtitle="Per locatie vs. maandbudget"
          >
            <div className="mb-2 px-4 pt-3 text-xs text-amber-800 bg-amber-50 mx-4 mt-3 rounded-md p-2 border border-amber-200" data-testid="cost-preview-note">
              <span className="font-semibold">Let op:</span> in preview wordt de backend (Emergent) gebruikt met geschatte
              tokens (char-heuristiek). In productie levert de Anthropic-SDK exacte tokencijfers en zijn deze cijfers
              feitelijk. Cap en € worden hier exact getoond zoals geboekt door <code className="rounded bg-amber-100 px-1">ai_cost_tracker</code>.
            </div>
            <div className="overflow-x-auto px-1">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Locatie</th>
                    <th className="px-4 py-2 text-right">Calls</th>
                    <th className="px-4 py-2 text-right">Tokens in / uit</th>
                    <th className="px-4 py-2 text-right">Kost (€)</th>
                    <th className="px-4 py-2 text-right">Cap (€)</th>
                    <th className="px-4 py-2 w-1/3">Gebruik</th>
                  </tr>
                </thead>
                <tbody>
                  {(cost?.per_location || []).filter((r) => r.has_ai || r.calls_count > 0).length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-500">Nog geen AI-activiteit deze maand.</td></tr>
                  ) : cost.per_location.filter((r) => r.has_ai || r.calls_count > 0).map((r) => (
                    <tr key={r.location_id} className="border-t border-slate-100" data-testid={`cost-row-${r.location_id}`}>
                      <td className="px-4 py-2 font-medium text-slate-900">{r.location_name || r.location_id}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmtInt(r.calls_count)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">
                        {fmtInt(r.tokens_input)} / {fmtInt(r.tokens_output)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmtEUR(r.cost_eur)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">{fmtEUR(r.cap_eur)}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-3">
                          <ProgressBar percent={r.percent_used} />
                          <span
                            className={`text-xs tabular-nums ${
                              r.cap_reached ? 'font-semibold text-rose-700' : r.warning_threshold_reached ? 'font-semibold text-amber-700' : 'text-slate-500'
                            }`}
                          >
                            {r.percent_used}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Top errors */}
          {errs && errs.total_failed > 0 && (
            <Section
              testid="ai-health-errors"
              icon={AlertOctagon}
              title={`Top foutgroepen · ${errs.total_failed} mislukte call${errs.total_failed === 1 ? '' : 's'}`}
              subtitle="Gegroepeerd op eerste 80 tekens van de foutmelding"
            >
              <ul className="divide-y divide-slate-100">
                {errs.groups.map((g, i) => (
                  <li key={i} className="px-4 py-3 text-sm" data-testid={`error-group-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-mono text-xs text-slate-700 truncate">{g.error_prefix}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Locaties: {Object.entries(g.locations).map(([loc, n]) => `${loc} (${n})`).join(', ')}
                        </p>
                      </div>
                      <div className="shrink-0 text-right text-xs">
                        <div className="font-bold tabular-nums text-rose-700">{g.count}×</div>
                        <div className="text-slate-500">laatst: {fmtAbsoluteTime(g.last_seen_at)}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Recent jobs */}
          <Section
            testid="ai-health-recent"
            icon={Cpu}
            title="Laatste 20 calls"
            subtitle={recentLocation ? `Gefilterd op ${recentLocation}` : 'Alle locaties · nieuwste eerst'}
            action={
              <select
                value={recentLocation}
                onChange={(e) => setRecentLocation(e.target.value)}
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs"
                data-testid="recent-location-filter"
              >
                <option value="">Alle locaties</option>
                {(summary?.per_location || []).map((r) => (
                  <option key={r.location_id} value={r.location_id}>
                    {r.location_id} ({r.total})
                  </option>
                ))}
              </select>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Tijd</th>
                    <th className="px-3 py-2">Locatie</th>
                    <th className="px-3 py-2">Week</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Backend</th>
                    <th className="px-3 py-2 text-right">LLM</th>
                    <th className="px-3 py-2 text-right">E2E</th>
                    <th className="px-3 py-2 text-right">
                      Shifts
                      <InfoTip testid="shifts-col-tip" text="Geschreven / voorgesteld. Als Claude shifts voorstelde die ongeldig bleken (verkeerde medewerker, datum buiten week, etc.), staat 'gedropt N' eronder — dat is een veel beter signaal dan een lage ratio op zich." />
                    </th>
                    <th className="px-3 py-2">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {(recent?.items || []).length === 0 ? (
                    <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-slate-500">Geen recente calls{recentLocation ? ` voor ${recentLocation}` : ''}.</td></tr>
                  ) : recent.items.map((it) => {
                    const written = (it.inserted ?? 0) + (it.overwritten ?? 0);
                    const proposed = it.shifts_proposed_by_ai;
                    const dropped = it.conflicts_dropped;
                    return (
                      <tr key={it.job_id} className={`border-t border-slate-100 ${it.quality_warning ? 'bg-amber-50/40' : ''}`} data-testid={`recent-row-${it.job_id}`}>
                        <td className="px-3 py-1.5 whitespace-nowrap text-slate-600">{fmtAbsoluteTime(it.created_at)}</td>
                        <td className="px-3 py-1.5 font-medium text-slate-900">{it.location_id}</td>
                        <td className="px-3 py-1.5 text-slate-500">
                          {it.year}-W{String(it.week ?? '').padStart(2, '0')}
                          {it.overwrite && <span className="ml-1 rounded bg-slate-100 px-1 text-[10px]">overwrite</span>}
                        </td>
                        <td className="px-3 py-1.5">
                          <StatusBadge bucket={it.bucket} subBucket={it.sub_bucket} />
                          {it.quality_warning && (
                            <span className="ml-1 text-[10px] text-amber-700 font-medium">
                              {it.quality_warning === 'empty_plan' ? 'leeg plan' : 'veel gedropt'}
                            </span>
                          )}
                          {it.fallback_used && <span className="ml-1 text-[10px] text-amber-700">{it.fallback_reason || 'fallback'}</span>}
                        </td>
                        <td className="px-3 py-1.5"><BackendBadge backend={it.backend} /></td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtMs(it.latency_llm_ms)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtMs(it.latency_end_to_end_ms)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                          {proposed != null ? (
                            <div>
                              <div>{written}/{proposed}</div>
                              {dropped > 0 && (
                                <div className="text-[10px] text-rose-600">× {dropped} gedropt</div>
                              )}
                              {it.skipped_existing > 0 && (
                                <div className="text-[10px] text-slate-400">× {it.skipped_existing} al-aanwezig</div>
                              )}
                            </div>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-1.5">
                          {it.error ? (
                            <span className="block max-w-[260px] truncate font-mono text-[10px] text-rose-700" title={it.error}>
                              {it.error}
                            </span>
                          ) : (
                            <RawResponseLink jobId={it.job_id} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Footer source notes */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1" data-testid="ai-health-sources">
            <p>
              <span className="font-semibold">Bronnen:</span> success/fail/latency/recent uit <code className="rounded bg-white px-1 border border-slate-200">ai_jobs</code> (TTL 7 dagen).
              Kostenhistorie uit <code className="rounded bg-white px-1 border border-slate-200">ai_usage_tracking</code> (geen TTL).
            </p>
            <p>
              <span className="font-semibold">Read-only:</span> dit dashboard wijzigt niets en raakt de AI-pipeline of triple-guard niet aan.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// Inline component for the "view raw" link in recent table
function RawResponseLink({ jobId }) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(false);
  async function fetchRaw() {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai/jobs/${jobId}`, { params: { include_raw: true } });
      setRaw(data.raw_response || '(geen raw_response opgeslagen)');
      setOpen(true);
    } catch (e) {
      setRaw(`Fout bij ophalen: ${e?.response?.data?.detail || e.message}`);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }
  return (
    <>
      <button
        onClick={fetchRaw}
        disabled={loading}
        className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-800"
        data-testid={`raw-response-btn-${jobId}`}
      >
        <ExternalLink size={10} /> raw
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          onClick={() => setOpen(false)}
          data-testid="raw-response-modal"
        >
          <div
            className="max-h-[80vh] w-full max-w-3xl overflow-auto rounded-lg bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold">Raw response · {jobId}</p>
              <button onClick={() => setOpen(false)} className="text-sm text-slate-500" data-testid="raw-response-close">sluiten</button>
            </div>
            <pre className="whitespace-pre-wrap break-words rounded bg-slate-50 p-3 text-[11px] text-slate-700">{raw}</pre>
          </div>
        </div>
      )}
    </>
  );
}
